import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Alert, Badge, Card, Col, Form, InputGroup, Row, Spinner, Table, Button } from "react-bootstrap";
import axiosInstance from "../services/axiosInterceptor";
import { getToken } from "../services/authorize";

const COPY_STATUS_VARIANT = { available: "success", on_loan: "warning", reserved: "info", repair: "secondary", lost: "danger", withdrawn: "dark" };

export default function LibraryBookSearch() {
  const [keyword, setKeyword] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axiosInstance.get(`${process.env.REACT_APP_API}/library/getCategories`, {
          headers: { authorization: `Bearer ${getToken()}` }
        });
        setCategories(res.data || []);
      } catch {
        // categories are optional, ignore error
      }
    };
    fetchCategories();
  }, []);

  const handleSearch = useCallback(async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError("");
    setSearched(true);
    try {
      const params = new URLSearchParams({ q: keyword });
      if (categoryId) params.append("category_id", categoryId);
      const res = await axiosInstance.get(`${process.env.REACT_APP_API}/library/searchBooks?${params}`, {
        headers: { authorization: `Bearer ${getToken()}` }
      });
      setResults(res.data || []);
    } catch (err) {
      setError("ค้นหาหนังสือไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setLoading(false);
    }
  }, [keyword, categoryId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <h4 className="mb-3">ค้นหาหนังสือ</h4>

        <Card className="mb-4">
          <Card.Body>
            <Form onSubmit={handleSearch}>
              <Row className="g-2 align-items-end">
                <Col md={6}>
                  <Form.Label className="fw-semibold">คำค้นหา</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="ชื่อหนังสือ, ผู้แต่ง, ISBN, รหัสหนังสือ..."
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      onKeyDown={handleKeyDown}
                      autoFocus
                    />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold">หมวดหมู่</Form.Label>
                  <Form.Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                    <option value="">ทุกหมวดหมู่</option>
                    {categories.map((c) => (
                      <option key={c.category_id} value={c.category_id}>
                        {c.category_name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button type="submit" variant="primary" className="w-100" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <><i className="ri-search-2-line me-1" />ค้นหา</>}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}

        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2 text-muted">กำลังค้นหา...</p>
          </div>
        )}

        {!loading && searched && (
          <Card>
            <Card.Header className="fw-semibold d-flex justify-content-between align-items-center">
              <span>ผลการค้นหา</span>
              <Badge bg="secondary">{results.length} รายการ{results.length >= 100 ? " (แสดงสูงสุด 100)" : ""}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {results.length === 0 ? (
                <Alert variant="info" className="m-3 mb-0">ไม่พบหนังสือที่ตรงกับคำค้นหา</Alert>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "5%" }}>#</th>
                      <th style={{ width: "30%" }}>ชื่อหนังสือ</th>
                      <th style={{ width: "18%" }}>ผู้แต่ง</th>
                      <th style={{ width: "12%" }}>ISBN</th>
                      <th style={{ width: "14%" }}>หมวดหมู่</th>
                      <th style={{ width: "8%" }}>เลขเรียก</th>
                      <th style={{ width: "6%" }}>จำนวนเล่ม</th>
                      <th style={{ width: "7%" }}>พร้อมยืม</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((book, idx) => {
                      const available = Number(book.available_count || 0);
                      const total = Number(book.copy_count || 0);
                      return (
                        <tr key={book.title_id}>
                          <td className="text-muted">{idx + 1}</td>
                          <td>
                            <div className="fw-semibold">{book.title_name}</div>
                            {book.edition_no && <small className="text-muted">พิมพ์ครั้งที่ {book.edition_no}</small>}
                            {book.publish_year && <small className="text-muted ms-2">ปี {book.publish_year}</small>}
                          </td>
                          <td>{book.author_name || "-"}</td>
                          <td><small>{book.isbn || "-"}</small></td>
                          <td>
                            {book.category_name
                              ? <Badge bg="light" text="dark" className="border">{book.category_name}</Badge>
                              : <span className="text-muted">-</span>}
                          </td>
                          <td><small>{book.call_number || "-"}</small></td>
                          <td className="text-center">{total}</td>
                          <td className="text-center">
                            <Badge bg={available > 0 ? "success" : "danger"}>
                              {available > 0 ? `${available} เล่ม` : "ไม่ว่าง"}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        {!loading && !searched && (
          <div className="text-center py-5 text-muted">
            <i className="ri-search-2-line fs-1 d-block mb-2" />
            <p>กรอกคำค้นหาแล้วกดปุ่ม "ค้นหา" เพื่อแสดงผล</p>
          </div>
        )}
      </main>
      <Footer />
    </React.Fragment>
  );
}
